import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import request from 'helpers/request';
import useDataLayer from 'src/hooks/useDataLayer';
import Label from 'components/_utility/Label';
import Radio from 'components/_form/Radio/Radio';
import Checkbox from 'components/_form/Checkbox/Checkbox';
import useLabel from 'src/hooks/useLabel';
import { getGoalId, getSendToDealerData, lgpdSelector } from 'src/redux/app/selectors';
import { bikeModelSelector } from 'src/redux/bike/selectors';
import { useSelector } from 'react-redux';
import fetchGoal from 'helpers/goals';
import {
  Wrap,
  H4,
  Tagline,
  _Input as Input,
  Buttons,
  PrimaryButton,
  SecondaryButton,
  InputError,
  Checkboxes,
  CheckboxWrapper,
  Radios,
  Text,
  ChevronDown,
  ContentToggle,
  AdditionalContent,
  SuccessMessage,
} from './SaveQuoteModal.styled';

const API_ORIGIN = window.location.origin.includes('localhost')
  ? 'https://www.triumph-test.info'
  : window.location.origin;

const SaveQuoteSchema = isLgpdActive =>
  Yup.object().shape({
    email: Yup.string()
      .email('invalidEmailLabel')
      .required('invalidEmailLabel'),
    recieveCommunication: isLgpdActive ? undefined : Yup.bool().oneOf([true], 'recieveCommunicationRequired'),
    agreeToTerms: isLgpdActive ? Yup.bool().oneOf([true], 'agreeToTermsRequiredLGPD') : undefined,
    privacyConsent: isLgpdActive ? undefined : Yup.bool().oneOf([true], 'agreeToTermsRequired'),
  });

const SaveQuoteModal = ({ secondaryActionFunc }) => {
  const getLabel = useLabel();
  const pushToDataLayer = useDataLayer();
  const lgpd = useSelector(lgpdSelector);
  const model = useSelector(bikeModelSelector);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showLgpdContent, setShowLgpdContent] = useState(false);
  const [title, setTitle] = useState();
  const [description, setDescription] = useState();
  const [saveButtonLabel, setSaveButtonLabel] = useState();
  const [cancelButtonLabel, setCancelButtonLabel] = useState();
  const [emailPlaceholder, setEmailPlaceholder] = useState();
  const [emailValidationErrorMessage, setEmailValidationErrorMessage] = useState();
  const [campaignId, setCampaignId] = useState();
  const [viewGoalId, setViewGoalId] = useState();
  const [successMessage, setSuccessMessage] = useState();
  const sendToDealerData = useSelector(getSendToDealerData);
  const quoteLink = window.localStorage.getItem('quoteLink');
  const bikeId = window.localStorage.getItem('bikeId');
  const viewSaveQuoteGoalId = useSelector(getGoalId('view Save Quote'));
  const submitSaveQuoteGoalId = useSelector(getGoalId('submit Save Quote'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          mode: 'cors',
        };

        const contentRes = await request(`/api/v2/finance/quotecontent`, requestOptions);

        setTitle(contentRes.data.Title);
        setDescription(contentRes.data.Description);
        setSaveButtonLabel(contentRes.data.SaveButtonLabel);
        setCancelButtonLabel(contentRes.data.CancelButtonLabel);
        setEmailPlaceholder(contentRes.data.EmailPlaceholder);
        setEmailValidationErrorMessage(contentRes.data.EmailValidationErrorMessage);
        setCampaignId(contentRes.data.CampaignId);
        setViewGoalId(contentRes.data.ViewGoalId);
      } catch (ex) {
        console.error(ex);
      }
    };

    fetchData();

    fetchGoal(viewSaveQuoteGoalId);
  }, []);

  const handleSubmit = values => {
    const postBody = {
      Email: values.email,
      Link: quoteLink || `checkSum=${bikeId}`,
      Goal: viewGoalId,
      CampaignId: campaignId,
      ConsentEmail: !!values.receiveCommunication,
      BikeId: model?.id,
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postBody),
    };

    fetch(`${API_ORIGIN}/api/v2/finance/quote`, requestOptions)
      .then(response => response.json())
      .then(response => {
        setSuccessMessage(response.Message);
        setIsSubmitted(true);
        pushToDataLayer('saveQuoteComplete');
        fetchGoal(submitSaveQuoteGoalId);
      })
      .catch(error => console.error(error));
  };

  return (
    <Formik
      initialValues={{
        email: '',
        dataConsent: false,
        dataConsentOptIn: '',
        dataConsentDoNotContact: '',
        receiveCommunication: false,
        agreeToTerms: false,
        privacyConsent: false,
      }}
      validationSchema={SaveQuoteSchema(!!lgpd)}
      onSubmit={values => handleSubmit(values)}
    >
      {({ values, errors, touched, handleChange, handleBlur, setValues }) => (
        <Form>
          <Wrap>
            <H4>{title}</H4>
            {!isSubmitted ? <Tagline>{description}</Tagline> : null}
            <Input
              type="text"
              name="email"
              value={values.email}
              onChange={handleChange}
              placeholder={emailPlaceholder}
              data-testid="emailInput"
            ></Input>
            {touched.email && errors.email ? <InputError>{emailValidationErrorMessage}</InputError> : null}
            {!isSubmitted && lgpd ? (
              <>
                <Text>
                  <Label name="dataConsentLabel"></Label>
                </Text>
                <ContentToggle
                  role="button"
                  onClick={() => setShowLgpdContent(!showLgpdContent)}
                  isActive={showLgpdContent}
                >
                  <Label name="moreContentToggleLabel"></Label>
                  <ChevronDown />
                </ContentToggle>
                <AdditionalContent isActive={showLgpdContent}>
                  <Label name="dataConsentAdditionalContentLabel"></Label>
                </AdditionalContent>
                <Radios>
                  <Radio
                    id="dataConsentOptIn"
                    checked={values.dataConsentOptIn}
                    value="dataConsentOptIn"
                    name="dataConsent"
                    onChange={() =>
                      setValues(prevValues => ({
                        ...prevValues,
                        dataConsent: true,
                        dataConsentOptIn: true,
                        dataConsentDoNotContact: false,
                      }))
                    }
                  >
                    <Label name="dataConsentOptInLabel"></Label>
                  </Radio>
                  <Radio
                    id="dataConsentDoNotContact"
                    checked={values.dataConsentDoNotContact}
                    value="dataConsentDoNotContact"
                    name="dataConsent"
                    onChange={() =>
                      setValues(prevValues => ({
                        ...prevValues,
                        dataConsent: true,
                        dataConsentOptIn: false,
                        dataConsentDoNotContact: true,
                      }))
                    }
                  >
                    <Label name="dataConsentDoNotContactLabel"></Label>
                  </Radio>
                  {touched.dataConsent && errors.dataConsent && (
                    <InputError>
                      <Label name={errors.dataConsent}></Label>
                    </InputError>
                  )}
                </Radios>
                <Checkboxes>
                  {values.dataConsentOptIn && (
                    <CheckboxWrapper>
                      <Checkbox
                        id="receiveCommunication"
                        name="receiveCommunication"
                        checked={values.receiveCommunication}
                        onChange={handleChange}
                      >
                        <Label name="receiveCommunicationLabel"></Label>
                      </Checkbox>
                    </CheckboxWrapper>
                  )}
                  <CheckboxWrapper>
                    <Checkbox
                      id="agreeToTerms"
                      name="agreeToTerms"
                      checked={values.agreeToTerms}
                      onChange={handleChange}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getLabel(
                            'agreeToTermsLabel',
                            'I have read and understand the Triumph {0}, {1}, and {2}'
                          )
                            .replace(
                              '{0}',
                              `<a href="${lgpd.privacyPolicyLink}" target="_blank">${getLabel(
                                'privacyPolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{1}',
                              `<a href="${lgpd.cookiePolicyLink}" target="_blank">${getLabel(
                                'cookiePolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{2}',
                              `<a href="${lgpd.termsAndConditionsLink}" target="_blank">${getLabel(
                                'termsAndConditionsLinkLabel'
                              )}</a>`
                            ),
                        }}
                      />
                    </Checkbox>
                    {touched.agreeToTerms && errors.agreeToTerms && (
                      <InputError>
                        <Label name={errors.agreeToTerms}></Label>
                      </InputError>
                    )}
                  </CheckboxWrapper>
                </Checkboxes>
              </>
            ) : null}
            {!isSubmitted && !lgpd ? (
              <>
                <>
                  <CheckboxWrapper>
                    <Checkbox
                      name="receiveCommunication"
                      id="receiveCommunication"
                      checked={values.receiveCommunication}
                      onChange={handleChange}
                    >
                      <Label name="receiveCommunicationsLabel"></Label>
                    </Checkbox>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <Checkbox
                      name="privacyConsent"
                      id="privacyConsent"
                      checked={values.privacyConsent}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getLabel('privacyPolicyDescription', 'agree to {0}').replace(
                            '{0}',
                            `<a href="${sendToDealerData.privacyPolicy}" target="_blank">${getLabel(
                              'privacyPolicyLabel'
                            )}</a>`
                          ),
                        }}
                      />
                    </Checkbox>
                    {touched.privacyConsent && errors.privacyConsent && (
                      <InputError>
                        <Label name="requiredLabel"></Label>
                      </InputError>
                    )}
                  </CheckboxWrapper>
                </>
              </>
            ) : null}
            {!isSubmitted ? (
              <Buttons>
                <PrimaryButton primary data-testid="primaryAction" type="submit">
                  {saveButtonLabel}
                </PrimaryButton>
                <SecondaryButton onClick={secondaryActionFunc} data-testid="secondaryAction">
                  {cancelButtonLabel}
                </SecondaryButton>
              </Buttons>
            ) : null}
            {isSubmitted ? <SuccessMessage>{successMessage}</SuccessMessage> : null}
          </Wrap>
        </Form>
      )}
    </Formik>
  );
};

export default SaveQuoteModal;
